import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { Button, Input, Result, Spin, Table, Tag, message } from "antd"
import { useTranslation } from "react-i18next"
import moment from "moment"
import {
  GET_PUBLIC_INVOICE,
  CONFIRM_PUBLIC_INVOICE,
} from "../../rest-api/queries/deliveries"
import { numberWithSpaces } from "../../hooks/numberWithSpaces"
import "./index.less"

/**
 * Mijozga yuboriladigan invoys sahifasi.
 *
 * Bu sahifa tizimga kirishni talab qilmaydi: havolani bilgan odam ochadi.
 * Shuning uchun bu yerda faqat yuk haqidagi ma'lumot ko'rsatiladi —
 * backend ham shu maydonlarnigina qaytaradi.
 */
export default function PublicInvoice() {
  const { token } = useParams<{ token: string }>()
  const { t } = useTranslation()

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<any>(null)
  const [notFound, setNotFound] = useState(false)
  const [name, setName] = useState("")
  const [confirming, setConfirming] = useState(false)

  async function load() {
    try {
      const res = await GET_PUBLIC_INVOICE(token)
      if (res?.success && res.data) {
        setData(res.data)
      } else {
        setNotFound(true)
      }
    } catch {
      setNotFound(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  async function handleConfirm() {
    if (!name.trim()) {
      message.warning(String(t("invEnterName")))
      return
    }
    setConfirming(true)
    try {
      const res = await CONFIRM_PUBLIC_INVOICE({
        token,
        confirmedBy: name.trim(),
      })
      if (res?.success) {
        message.success(String(t("invConfirmed")))
        await load()
      }
    } finally {
      setConfirming(false)
    }
  }

  if (loading) {
    return (
      <div className="public-invoice-center">
        <Spin size="large" />
      </div>
    )
  }

  if (notFound || !data) {
    return (
      <div className="public-invoice-center">
        <Result status="404" title={t("invNotFound")} subTitle={t("invNotFoundHint")} />
      </div>
    )
  }

  const isConfirmed = Boolean(data.publicConfirmedTime)
  const items = data.items || []

  const columns = [
    { title: t("product"), dataIndex: "productName", key: "productName" },
    { title: t("article"), dataIndex: "article", key: "article" },
    {
      title: t("amount"),
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (v: number, r: any) => `${numberWithSpaces(v || 0)} ${r.unitName || ""}`,
    },
    {
      title: t("priceUSD"),
      dataIndex: "priceUSD",
      key: "priceUSD",
      align: "right" as const,
      render: (v: number) => `$ ${numberWithSpaces(v || 0)}`,
    },
    {
      title: t("total"),
      dataIndex: "totalUSD",
      key: "totalUSD",
      align: "right" as const,
      render: (v: number) => `$ ${numberWithSpaces(v || 0)}`,
    },
  ]

  return (
    <div className="public-invoice">
      <div className="public-invoice-card">
        <div className="public-invoice-head">
          <div>
            <div className="public-invoice-title">{t("invTitle")}</div>
            <div className="public-invoice-number">{data.deliveryNumber}</div>
          </div>
          {isConfirmed ? (
            <Tag color="success" className="public-invoice-badge">
              {t("invStatusConfirmed")}
            </Tag>
          ) : (
            <Tag color="processing" className="public-invoice-badge">
              {t("invStatusPending")}
            </Tag>
          )}
        </div>

        <div className="public-invoice-grid">
          <InfoRow label={t("counterparty")} value={data.counterpartyName} />
          <InfoRow label={t("phone")} value={data.counterpartyPhone} />
          <InfoRow label={t("invAddress")} value={data.deliveryAddress} />
          <InfoRow label={t("warehouse")} value={data.warehouseName} />
          <InfoRow label={t("invDriver")} value={data.driverName} />
          <InfoRow label={t("invDriverPhone")} value={data.driverPhone} />
          <InfoRow
            label={t("invVehicle")}
            value={[data.vehicleModel, data.vehiclePlate].filter(Boolean).join(" · ")}
          />
          <InfoRow
            label={t("invDeparture")}
            value={
              data.departureTime
                ? moment(data.departureTime).format("DD.MM.YYYY HH:mm")
                : ""
            }
          />
          <InfoRow label={t("invCargo")} value={data.cargoInfo} />
          <InfoRow label={t("paymentType")} value={data.paymentType} />
        </div>

        {items.length > 0 && (
          <Table
            className="public-invoice-table"
            columns={columns}
            dataSource={items.map((x: any, i: number) => ({ ...x, key: i }))}
            pagination={false}
            size="middle"
            scroll={{ x: "max-content" }}
          />
        )}

        <div className="public-invoice-total">
          <span>{t("invTotal")}</span>
          <strong>$ {numberWithSpaces(data.totalAmountUSD || 0)}</strong>
        </div>

        {isConfirmed ? (
          <div className="public-invoice-confirmed">
            <div>{t("invConfirmedBy", { name: data.confirmedBy || "" })}</div>
            <div className="public-invoice-confirmed-time">
              {moment(data.publicConfirmedTime).format("DD.MM.YYYY HH:mm")}
            </div>
          </div>
        ) : (
          <div className="public-invoice-confirm-box">
            <div className="public-invoice-confirm-hint">{t("invConfirmHint")}</div>
            <Input
              size="large"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={String(t("invYourName"))}
            />
            <Button
              type="primary"
              size="large"
              block
              loading={confirming}
              onClick={handleConfirm}
            >
              {t("invConfirmButton")}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

function InfoRow({ label, value }: { label: any; value?: string }) {
  if (!value) return null
  return (
    <div className="public-invoice-row">
      <span className="public-invoice-row-label">{label}</span>
      <span className="public-invoice-row-value">{value}</span>
    </div>
  )
}
